// Hotel Itinerary Update — ops edit screen (Hotel Admin Edit).
//
// Flow (per the backend "Hotel Admin Edit — Frontend Integration Guide"):
//   1. Ops enters an Itinerary ID; "Modified By" is pre-filled with the
//      logged-in email (read-only).
//   2. Fetch -> API 1 (/hotel/adminedit/fetch) populates every editable field.
//   3. Ops edits fields; arrays (hotels / guests / payments) are repeatable.
//   4. Save -> API 2 (/hotel/adminedit/update) with the edited payload +
//      modified_by. Each array row keeps its numeric `id`.
//   5. On 200 we re-fetch to confirm; on 400 we show details.validationErrors.
//
// The full form is always visible: before a booking is fetched every field is
// shown but disabled (populated from an empty placeholder). As soon as the
// response arrives the fields re-populate and become editable.
//
// Design follows the Flight Management screen: a `feeModalForm` retrieval bar
// with a dark `searchBtn`, then collapsible Accordions whose repeatable rows use
// the shared `segmentCard` / `segmentHeader` (flightRoute + statusBadge) styling.
//
// PNR search is NOT supported yet (hotel bookings have no PNR in the current
// schema) — only the Itinerary ID is functional.
import React, { useState } from 'react';
import { Box, Button, Typography, Alert, Snackbar, CircularProgress } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  fetchHotelBooking,
  updateHotelBooking,
  getModifiedBy,
  HotelBookingData,
  HotelTransaction,
  HotelDetail,
  HotelPassenger,
  HotelPaymentBreakdown,
  HotelPaymentDetail,
  UpdateHotelPayload,
} from '../operations/hotelEditService';
import { Field, SelectField } from '../components/manual-booking/fields';
import { extractApiError, isSuccessStatus, BOARD_TYPES, TITLES } from '../components/manual-booking/types';
// @ts-ignore -- helpers is JS
import { validateEmail } from '../helpers';

//********CONSTANTS STARTS*********/
const PAX_TYPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'ADT', label: 'Adult (ADT)' },
  { value: 'CHD', label: 'Child (CHD)' },
  { value: 'INF', label: 'Infant (INF)' },
];
const BOARD_TYPE_OPTIONS = [
  { value: '', label: '—' },
  ...BOARD_TYPES.map((b) => ({ value: b.value as string, label: b.label })),
];
const TITLE_OPTIONS = [{ value: '', label: '—' }, ...TITLES.map((t) => ({ value: t, label: t }))];
const YES_NO_OPTIONS = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
];
//********CONSTANTS ENDS*********/

//********HELPERS STARTS*********/
// Display any DB value (string | number | null) as a controlled input string.
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
// Normalize a 0/1/true/false boolean-ish DB value to the '1'/'0' select value.
const boolStr = (v: unknown): string => (v === 1 || v === true || v === '1' ? '1' : '0');
//********HELPERS ENDS*********/

// A repeatable card matching the Flight Management `segmentCard` styling: a
// bordered card with a header (title on the left, optional badge on the right).
// The `.feeModalForm` wrapper is what activates the flex/grid layout on
// `.feeInputFieldWrap` (see global.css) — without it the fields stack full-width.
const SegmentCard: React.FC<{ title: string; badge?: string; children: React.ReactNode }> = ({
  title,
  badge,
  children,
}) => (
  <Box className="feeModalForm">
    <Box className="segmentCard" sx={{ mb: 2.5, pb: '20px !important' }}>
      <div className="segmentHeader">
        <div className="flightRoute">{title}</div>
        {badge ? (
          <div className="statusBadge" style={{ backgroundColor: '#eef2f7', color: '#2c3e50' }}>
            {badge}
          </div>
        ) : null}
      </div>
      <Box className="feeInputFieldWrap" sx={{ mb: '0 !important' }}>
        {children}
      </Box>
    </Box>
  </Box>
);

// Multiline text field reusing the shared form CSS (Field is single-line only).
const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
  <div className="form-control inputFieldWrap" style={{ flexBasis: '100%', maxWidth: '100%' }}>
    <label>{label}</label>
    <textarea
      className="inputField"
      value={value}
      rows={3}
      disabled={disabled}
      style={{
        resize: 'vertical',
        minHeight: 70,
        fontFamily: 'inherit',
        ...(disabled ? { backgroundColor: '#f7fcff', cursor: 'not-allowed' } : {}),
      }}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// Placeholder shown before a booking is fetched: one empty row per repeatable
// section so the full form is visible (disabled) up front.
const emptyView = (): HotelBookingData => ({
  itinerary_id: '',
  transaction: {},
  hotel_details: [{ id: 0 }],
  hotel_passenger_details: [{ id: 0 }],
  payment_breakdown: { id: 0 },
  payment_details: [{ id: 0 }],
});

const HotelItineraryUpdate: React.FC = () => {
  const [searchId, setSearchId] = useState('');
  const [modifiedBy] = useState(getModifiedBy());

  const [data, setData] = useState<HotelBookingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  const notify = (msg: string, sev: 'success' | 'error') => setSnack({ open: true, msg, sev });

  //********NESTED-FIELD UPDATERS STARTS*********/
  const setTxField = (k: keyof HotelTransaction, v: unknown) =>
    setData((d) => (d ? { ...d, transaction: { ...(d.transaction || {}), [k]: v } } : d));

  const setHotelField = (i: number, k: keyof HotelDetail, v: unknown) =>
    setData((d) => {
      if (!d) return d;
      const hotel_details = d.hotel_details.map((h, idx) => (idx === i ? { ...h, [k]: v } : h));
      return { ...d, hotel_details };
    });

  const setPaxField = (i: number, k: keyof HotelPassenger, v: unknown) =>
    setData((d) => {
      if (!d) return d;
      const hotel_passenger_details = d.hotel_passenger_details.map((p, idx) =>
        idx === i ? { ...p, [k]: v } : p
      );
      return { ...d, hotel_passenger_details };
    });

  const setBreakdownField = (k: keyof HotelPaymentBreakdown, v: unknown) =>
    setData((d) =>
      d && d.payment_breakdown
        ? { ...d, payment_breakdown: { ...d.payment_breakdown, [k]: v } }
        : d
    );

  const setPayField = (i: number, k: keyof HotelPaymentDetail, v: unknown) =>
    setData((d) => {
      if (!d) return d;
      const payment_details = d.payment_details.map((p, idx) =>
        idx === i ? { ...p, [k]: v } : p
      );
      return { ...d, payment_details };
    });
  //********NESTED-FIELD UPDATERS ENDS*********/

  //********FETCH (API 1)*********/
  const runFetch = async () => {
    const id = searchId.trim();
    if (!id) {
      notify('Enter an Itinerary ID to fetch.', 'error');
      return;
    }
    setLoading(true);
    setValidationErrors([]);
    try {
      const res = await fetchHotelBooking(id);
      if (isSuccessStatus(res?.status) && res?.data) {
        setData(res.data);
        notify(res.message || 'Hotel booking details fetched successfully.', 'success');
      } else {
        setData(null);
        const { message, errors } = extractApiError(res);
        setValidationErrors(errors);
        notify(message, 'error');
      }
    } catch (err) {
      setData(null);
      const { message, errors } = extractApiError(err);
      setValidationErrors(errors);
      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  //********SAVE (API 2)*********/
  const runSave = async () => {
    if (!data) return;
    setValidationErrors([]);

    // Light client-side guards for the two fields the UI owns. Row-`id` /
    // shape validation is left to the backend (it returns the authoritative
    // details.validationErrors) — the fetched rows are sent back unchanged, so
    // whatever ids came from API 1 are preserved on save.
    const problems: string[] = [];
    if (!searchId.trim()) problems.push("Missing 'itinerary_id'");
    if (!validateEmail(modifiedBy)) problems.push("'modified_by' must be a valid email");
    if (!data.hotel_details?.length) problems.push("'hotel_details' must have at least one row");
    if (problems.length) {
      setValidationErrors(problems);
      notify('Please fix the highlighted problems before saving.', 'error');
      return;
    }

    const payload: UpdateHotelPayload = {
      itinerary_id: searchId.trim(),
      modified_by: modifiedBy,
      transaction: data.transaction,
      hotel_details: data.hotel_details,
      hotel_passenger_details: data.hotel_passenger_details,
      payment_breakdown: data.payment_breakdown,
      payment_details: data.payment_details,
    };

    setSaving(true);
    try {
      const res = await updateHotelBooking(payload);
      if (isSuccessStatus(res?.status)) {
        notify(res.message || 'Hotel booking details updated successfully.', 'success');
        await runFetch(); // re-fetch to confirm the persisted values
      } else {
        const { message, errors } = extractApiError(res);
        setValidationErrors(errors);
        notify(message, 'error');
      }
    } catch (err) {
      const { message, errors } = extractApiError(err);
      setValidationErrors(errors);
      notify(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Before a booking is fetched, render the full form from a placeholder and
  // keep every input disabled. `disabled` is the flag passed to each field.
  const view = data ?? emptyView();
  const enabled = !!data;
  const disabled = !enabled;
  const tx = view.transaction;
  const pb = view.payment_breakdown;

  return (
    <Box className="flightManagementWrap">
      <Box className="headingBtnWrap">
        <Typography variant="h1">Hotel Itinerary Update</Typography>
      </Box>

      {/* ---------------------------------------------- Retrieval bar */}
      <Box className="feeModalForm" style={{ marginTop: '20px' }}>
        <Typography variant="subtitle1" gutterBottom>
          Booking Retrieval
        </Typography>
        <Box className="feeInputFieldWrap" sx={{ alignItems: 'flex-start' }}>
          <div className="form-control inputFieldWrap">
            <label>
              Itinerary ID<span style={{ color: '#e31a24' }}> *</span>
            </label>
            <input
              type="text"
              className="inputField"
              value={searchId}
              placeholder="e.g. 3f9a2b7c8d1e4f5a9b0c1d2e3f4a5b6c"
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runFetch();
              }}
            />
          </div>
          <div className="form-control inputFieldWrap">
            <label>Modified By</label>
            <input
              type="text"
              className="inputField"
              value={modifiedBy}
              readOnly
              placeholder="logged-in user"
              style={{ backgroundColor: '#f7fcff', cursor: 'not-allowed' }}
            />
          </div>
          <div className="searchInputFilterBox fetchPNR">
            <Button
              variant="contained"
              className="searchBtn"
              type="button"
              onClick={runFetch}
              disabled={loading || !searchId.trim()}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loading ? 'Loading...' : 'Fetch'}
            </Button>
          </div>
        </Box>
      </Box>

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {validationErrors.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </Alert>
      )}

      {!enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter an Itinerary ID and click <b>Fetch</b> to load a booking. The sections below become
          editable once the booking is loaded.
        </Alert>
      )}

      {/* TEMP DEBUG — raw fetched payload, so we can confirm the backend's field
          names (esp. the row `id` keys). Remove once the shape is confirmed. */}
      {data && (
        <details style={{ marginBottom: 16, border: '1px solid #e1e8ed', borderRadius: 8, padding: 12, background: '#fff' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>DEBUG: raw fetched response</summary>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 12, marginTop: 8 }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      )}

      {/* ---------------------------------------------- Transaction */}
      <Accordion className="flightMgmt" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="span">
            Transaction{enabled && view.itinerary_id ? ` — ${str(view.itinerary_id)}` : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SegmentCard
            title={view.itinerary_id ? `Itinerary ${str(view.itinerary_id)}` : 'Transaction'}
            badge={enabled ? [tx?.bktype, tx?.itinerary_status].filter(Boolean).map(String).join(' • ') : undefined}
          >
            <Field label="Itinerary Status" disabled={disabled} value={str(tx?.itinerary_status)} onChange={(v) => setTxField('itinerary_status', v)} />
            <Field label="Internal Status" disabled={disabled} value={str(tx?.internal_status)} onChange={(v) => setTxField('internal_status', v)} />
            <Field label="Payment Status" disabled={disabled} value={str(tx?.payment_status)} onChange={(v) => setTxField('payment_status', v)} />
            <Field label="Travel Start Date" type="date" disabled={disabled} value={str(tx?.travel_start_date)} onChange={(v) => setTxField('travel_start_date', v)} />
            <Field label="Travel End Date" type="date" disabled={disabled} value={str(tx?.travel_end_date)} onChange={(v) => setTxField('travel_end_date', v)} />
            <Field label="Total User Amount" disabled={disabled} value={str(tx?.total_usr_amt)} onChange={(v) => setTxField('total_usr_amt', v)} />
            <Field label="Supplier Total Amount" disabled={disabled} value={str(tx?.sup_total_amt)} onChange={(v) => setTxField('sup_total_amt', v)} />
            <Field label="US Currency Total" disabled={disabled} value={str(tx?.us_cur_total_amt)} onChange={(v) => setTxField('us_cur_total_amt', v)} />
          </SegmentCard>
        </AccordionDetails>
      </Accordion>

      {/* ---------------------------------------------- Hotel details (array) */}
      <Accordion className="flightMgmt" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="span">Hotel Details ({view.hotel_details.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {view.hotel_details.map((h, i) => (
            <SegmentCard
              key={h.id ?? i}
              title={h.hotel_name ? String(h.hotel_name) : `Hotel ${i + 1}`}
              badge={enabled ? `id: ${str(h.id)}` : undefined}
            >
              <Field label="Hotel Name" disabled={disabled} value={str(h.hotel_name)} onChange={(v) => setHotelField(i, 'hotel_name', v)} />
              <Field label="Country" disabled={disabled} value={str(h.country)} onChange={(v) => setHotelField(i, 'country', v)} />
              <Field label="City" disabled={disabled} value={str(h.city)} onChange={(v) => setHotelField(i, 'city', v)} />
              <Field label="Check In" type="date" disabled={disabled} value={str(h.check_in)} onChange={(v) => setHotelField(i, 'check_in', v)} />
              <Field label="Check Out" type="date" disabled={disabled} value={str(h.check_out)} onChange={(v) => setHotelField(i, 'check_out', v)} />
              <Field label="Status" disabled={disabled} value={str(h.status)} onChange={(v) => setHotelField(i, 'status', v)} />
              <Field label="Internal Status" disabled={disabled} value={str(h.internal_status)} onChange={(v) => setHotelField(i, 'internal_status', v)} />
              <Field label="No. of Rooms" type="number" min={0} disabled={disabled} value={str(h.no_of_rooms)} onChange={(v) => setHotelField(i, 'no_of_rooms', v)} />
              <Field label="Adult Count" type="number" min={0} disabled={disabled} value={str(h.adult_count)} onChange={(v) => setHotelField(i, 'adult_count', v)} />
              <Field label="Child Count" type="number" min={0} disabled={disabled} value={str(h.child_count)} onChange={(v) => setHotelField(i, 'child_count', v)} />
              <Field label="Child Ages" disabled={disabled} value={str(h.child_ages)} onChange={(v) => setHotelField(i, 'child_ages', v)} placeholder="e.g. 5,8" />
              <Field label="Room Name" disabled={disabled} value={str(h.room_name)} onChange={(v) => setHotelField(i, 'room_name', v)} />
              <Field label="Room ID" disabled={disabled} value={str(h.room_id)} onChange={(v) => setHotelField(i, 'room_id', v)} />
              <SelectField label="Board Type" disabled={disabled} value={str(h.board_type)} onChange={(v) => setHotelField(i, 'board_type', v)} options={BOARD_TYPE_OPTIONS} />
              <SelectField label="Refundable" disabled={disabled} value={boolStr(h.is_refundable)} onChange={(v) => setHotelField(i, 'is_refundable', Number(v))} options={YES_NO_OPTIONS} />
              <Field label="Supplier Base Fare" disabled={disabled} value={str(h.sup_base_fare)} onChange={(v) => setHotelField(i, 'sup_base_fare', v)} />
              <Field label="Supplier Taxes" disabled={disabled} value={str(h.sup_taxes)} onChange={(v) => setHotelField(i, 'sup_taxes', v)} />
              <Field label="Supplier Total Fare" disabled={disabled} value={str(h.sup_total_fare)} onChange={(v) => setHotelField(i, 'sup_total_fare', v)} />
              <TextAreaField label="Cancellation Policy" disabled={disabled} value={str(h.cancellation_policy)} onChange={(v) => setHotelField(i, 'cancellation_policy', v)} />
            </SegmentCard>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* ---------------------------------------------- Guests (array) */}
      <Accordion className="flightMgmt" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="span">Guest Details ({view.hotel_passenger_details.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {view.hotel_passenger_details.map((p, i) => (
            <SegmentCard
              key={p.id ?? i}
              title={p.first_name ? `${str(p.first_name)} ${str(p.last_name)}`.trim() : `Guest ${i + 1}`}
              badge={enabled ? `${boolStr(p.is_lead_pax) === '1' ? 'Lead • ' : ''}id: ${str(p.id)}` : undefined}
            >
              <SelectField label="Title" disabled={disabled} value={str(p.title)} onChange={(v) => setPaxField(i, 'title', v)} options={TITLE_OPTIONS} />
              <Field label="First Name" disabled={disabled} value={str(p.first_name)} onChange={(v) => setPaxField(i, 'first_name', v)} />
              <Field label="Middle Name" disabled={disabled} value={str(p.middle_name)} onChange={(v) => setPaxField(i, 'middle_name', v)} />
              <Field label="Last Name" disabled={disabled} value={str(p.last_name)} onChange={(v) => setPaxField(i, 'last_name', v)} />
              <Field label="Email" type="email" disabled={disabled} value={str(p.email)} onChange={(v) => setPaxField(i, 'email', v)} />
              <Field label="Country Code" disabled={disabled} value={str(p.country_code)} onChange={(v) => setPaxField(i, 'country_code', v)} placeholder="+1" />
              <Field label="Phone No." disabled={disabled} value={str(p.phone_no)} onChange={(v) => setPaxField(i, 'phone_no', v)} />
              <SelectField label="Pax Type" disabled={disabled} value={str(p.pax_type)} onChange={(v) => setPaxField(i, 'pax_type', v)} options={PAX_TYPE_OPTIONS} />
              <Field label="Room No." type="number" min={1} disabled={disabled} value={str(p.room_no)} onChange={(v) => setPaxField(i, 'room_no', v)} />
              <Field label="Country Name" disabled={disabled} value={str(p.country_name)} onChange={(v) => setPaxField(i, 'country_name', v)} />
              <Field label="ISO" disabled={disabled} value={str(p.iso)} onChange={(v) => setPaxField(i, 'iso', v)} placeholder="US" />
              <SelectField label="Lead Guest" disabled={disabled} value={boolStr(p.is_lead_pax)} onChange={(v) => setPaxField(i, 'is_lead_pax', Number(v))} options={YES_NO_OPTIONS} />
            </SegmentCard>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* ---------------------------------------------- Payment breakdown (single) */}
      {pb && (
        <Accordion className="flightMgmt" defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="span">Payment Breakdown</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SegmentCard title="Payment Breakdown" badge={enabled ? `id: ${str(pb.id)}` : undefined}>
              <Field label="Hotel Price" disabled={disabled} value={str(pb.hotel_price)} onChange={(v) => setBreakdownField('hotel_price', v)} />
              <Field label="Total Price" disabled={disabled} value={str(pb.total_price)} onChange={(v) => setBreakdownField('total_price', v)} />
              <Field label="Discount Amount" disabled={disabled} value={str(pb.discount_amount)} onChange={(v) => setBreakdownField('discount_amount', v)} />
              <Field label="Actual Price" disabled={disabled} value={str(pb.actual_price)} onChange={(v) => setBreakdownField('actual_price', v)} />
              <Field label="Final Price" disabled={disabled} value={str(pb.final_price)} onChange={(v) => setBreakdownField('final_price', v)} />
              <Field label="Hotel Discount" disabled={disabled} value={str(pb.hotel_discount)} onChange={(v) => setBreakdownField('hotel_discount', v)} />
              <Field label="Hotel Check-in Due" disabled={disabled} value={str(pb.hotel_checkin_due)} onChange={(v) => setBreakdownField('hotel_checkin_due', v)} />
              <Field label="Promo Code" disabled={disabled} value={str(pb.promo_code)} onChange={(v) => setBreakdownField('promo_code', v)} />
              <SelectField label="Promo Applied" disabled={disabled} value={boolStr(pb.promo_applied)} onChange={(v) => setBreakdownField('promo_applied', Number(v))} options={YES_NO_OPTIONS} />
            </SegmentCard>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ---------------------------------------------- Payment details (array) */}
      <Accordion className="flightMgmt" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="span">Payment Details ({view.payment_details.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {view.payment_details.map((p, i) => (
            <SegmentCard
              key={p.id ?? i}
              title={`Payment ${i + 1}`}
              badge={enabled ? `${str(p.payment_status) || ''}${p.payment_status ? ' • ' : ''}id: ${str(p.id)}` : undefined}
            >
              <Field label="Payment Type" disabled={disabled} value={str(p.payment_type)} onChange={(v) => setPayField(i, 'payment_type', v)} />
              <Field label="Pay Amount" disabled={disabled} value={str(p.pay_amount)} onChange={(v) => setPayField(i, 'pay_amount', v)} />
              <Field label="Credit Balance" disabled={disabled} value={str(p.credit_balance)} onChange={(v) => setPayField(i, 'credit_balance', v)} />
              <Field label="Card Type" disabled={disabled} value={str(p.card_type)} onChange={(v) => setPayField(i, 'card_type', v)} />
              <Field label="Customer Name" disabled={disabled} value={str(p.customer_name)} onChange={(v) => setPayField(i, 'customer_name', v)} />
              <Field label="Currency Code" disabled={disabled} value={str(p.pay_curr_code)} onChange={(v) => setPayField(i, 'pay_curr_code', v)} placeholder="USD" />
              <Field label="Payment Status" disabled={disabled} value={str(p.payment_status)} onChange={(v) => setPayField(i, 'payment_status', v)} />
              <Field label="Payment Remarks" disabled={disabled} value={str(p.payment_remarks)} onChange={(v) => setPayField(i, 'payment_remarks', v)} />
            </SegmentCard>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* ---------------------------------------------- Save */}
      <Box className="searchInputFilterBox" display="flex" justifyContent="flex-end" mt={3} mb={4}>
        <Button
          variant="contained"
          className="searchBtn"
          onClick={runSave}
          disabled={saving || !enabled}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelItineraryUpdate;
